import { AnyAction } from "@reduxjs/toolkit";
import type { Notice } from "components/app-notices/types";
import type { ISysMsg } from "components/system-messages/types";
import { ActionType, Breakpoint, GameData, Job, Language, Modal, Theme, View } from "enums";
import { appEnv } from "managers/env";
import type { GameServers } from "managers/types/net-types";
import { AddCharacterModalOptions } from "modals/content/add-character/types";
import type { ConfirmationOptions } from "modals/content/confirmation/types";
import type { RecipeModalData } from "modals/content/recipes/types";
import React from "react";
import { createStore } from "redux";
import type { AppState } from "types/app";
import type { AppUser, Character, UserData, UserInfo, UserOptions } from "types/data";
import type { Obtained } from "types/game";

export const actions = {
	setBreakpoint: function(payload:string){ return { type: ActionType.SET_BREAKPOINT, payload }; },
	setView: function(payload:View){ return { type: ActionType.SET_VIEW, payload }; },
	addSystemMessage: function(payload:ISysMsg){ return { type: ActionType.ADD_SYS_MESSAGE, payload }; },
	removeSystemMessage: function(payload:number){ return { type: ActionType.REMOVE_SYS_MESSAGE, payload }; },
	setUserInfo: function(payload:UserInfo){ return { type: ActionType.SET_USER_INFO, payload }; },
	setUser: function(payload:AppUser){ return { type: ActionType.SET_USER, payload }; },
	setUserOptions: function(payload:UserOptions){ return { type: ActionType.SET_USER_OPTIONS, payload }; },
	setUserData: function(payload:UserData){ return { type: ActionType.SET_USER_DATA, payload }; },
	setCharacters: function(payload:Character[]){ return { type: ActionType.SET_CHARACTERS, payload }; },
	deleteCharacters: function(payload:string[]){ return { type: ActionType.DELETE_CHARACTERS, payload }; },
	setNotices: function(payload:Notice[]){ return { type: ActionType.SET_NOTICES, payload }; },
	setModalConfirmation: function(payload:ConfirmationOptions){ return { type: ActionType.SET_MODAL_CONFIRMATION, payload }; },
	setModalEnemies: function(payload:Obtained.Enemy[]){ return { type: ActionType.SET_MODAL_ENEMIES, payload }; },
	setModalRecipes: function(payload:RecipeModalData){ return { type: ActionType.SET_MODAL_RECIPES, payload }; },
	setModalMarkets: function(payload:Obtained.Market){ return { type: ActionType.SET_MODAL_MARKETS, payload }; },
	setModalExtractions: function(payload:Obtained.Extraction){ return { type: ActionType.SET_MODAL_EXTRACTIONS, payload }; },
	setModalGathering: function(payload:Obtained.Gathering[]){ return { type: ActionType.SET_MODAL_GATHERING, payload }; },
	setModalFishing: function(payload:Obtained.Fishing[]){ return { type: ActionType.SET_MODAL_FISHING, payload }; },
	setModalShops: function(payload:Obtained.Shop[]){ return { type: ActionType.SET_MODAL_SHOPS, payload }; },
	setModalLocations: function(payload:Obtained.Location[]){ return { type: ActionType.SET_MODAL_LOCATIONS, payload }; },
	setModalTrials: function(payload:Obtained.Trial[]){ return { type: ActionType.SET_MODAL_TRIALS, payload }; },
	setModalDungeons: function(payload:GameData.Dungeon[]){ return { type: ActionType.SET_MODAL_DUNGEONS, payload }; },
	setModalRaids: function(payload:GameData.Raid[]){ return { type: ActionType.SET_MODAL_RAIDS, payload }; },
	setModalFates: function(payload:Obtained.Fate[]){ return { type: ActionType.SET_MODAL_FATES, payload }; },
	setModalLeves: function(payload:GameData.Leve[]){ return { type: ActionType.SET_MODAL_LEVES, payload }; },
	setModalQuests: function(payload:GameData.Quest[]){ return { type: ActionType.SET_MODAL_QUESTS, payload }; },
	setModalOther: function(payload:Obtained.Other[]){ return { type: ActionType.SET_MODAL_OTHER, payload }; },
	setModalAddCharacter: function(payload:AddCharacterModalOptions){ return { type: ActionType.SET_MODAL_ADD_CHARACTER, payload }; },
	setModalRoulette: function(payload:GameData.Roulette[]){ return { type: ActionType.SET_MODAL_ROULETTE, payload }; },
	setModalDeepDungeon: function(payload:GameData.DeepDungeon[]){ return { type: ActionType.SET_MODAL_DEEP_DUNGEON, payload }; },
	setModalFieldOperation: function(payload:GameData.FieldOperation[]){ return { type: ActionType.SET_MODAL_FIELD_OPERATION, payload }; },
	setModalVariant: function(payload:GameData.Variant[]){ return { type: ActionType.SET_MODAL_VARIANT, payload }; },
	setAnonymousAvailable: function(payload:boolean){ return { type: ActionType.SET_ANONYMOUS_AVAILABLE, payload }; },
	setGameServers: function(payload:GameServers){ return { type: ActionType.SET_GAME_SERVERS, payload }; },
};

const initialState:AppState = {
	appVersion: appEnv.version,
	breakpoint: Breakpoint.DESKTOP,
	userInfo: {
		isAuthenticated: false,
		username: "",
		// options: manager.data.getDefaultOptions(), // Types refactor has triggered circular dependency error doing this. Options should be kept in sync if getDefaultOptions changes
		options: {
			theme: Theme.DARK,
			lang: Language.Site.EN,
			gameLang: Language.Game.EN,
			job: null,
			hideCompletedSteps: false,
			hideCompletedTasks: false,
			showMaximumItemQty: true,
			relics: {
				animusStepJobs: new Array(9).fill(Job.PLD),
				zodiacBravesStepJobs: new Array(4).fill(Job.PLD),
				zodiacZetaStepJob: Job.PLD,
				animaCompleteStepJob: Job.PLD,
				animaLuxStepJob: Job.PLD,
			},
			items: {
				hideNoRemaining: false,
				showOnlyBuyable: false,
				showMaximumRequired: false,
				selectedView: "Items",
				relicsFilter: [],
				relicPartsFilter: [],
				currencyFilter: [],
			},
			progress: {
				ignorePhyseosPart: false,
				compactProgressView: false,
			},
		},
		data: { version: appEnv.dataVersion, characters: [] },
	},
	maxOfflineCharacters: 3,
	view: View.HOME,
	systemMessages: [],
	notices: [],
	modal: {
		[Modal.CONFIRMATION]: { title: "", text: "", confirm: { text: "", action: () => {} }, cancel: { text: "", action: () => {} } },
		[Modal.ENEMIES]: [],
		[Modal.RECIPES]: { recipes: [], qtyNeeded: -1 },
		[Modal.MARKETS]: {},
		[Modal.EXTRACTIONS]: true,
		[Modal.GATHERING]: [],
		[Modal.FISHING]: [],
		[Modal.SHOPS]: [],
		[Modal.LOCATIONS]: [],
		[Modal.TRIALS]: [],
		[Modal.DUNGEONS]: [],
		[Modal.RAIDS]: [],
		[Modal.FATES]: [],
		[Modal.LEVES]: [],
		[Modal.QUESTS]: [],
		[Modal.OTHER]: [],
		[Modal.DESYNTHESIS]: [],
		[Modal.REDUCTIONS]: [],
		[Modal.ADD_CHARACTER]: { popoverContent: React.createElement("div", null, "No tooltip content") },
		[Modal.ROULETTE]: [],
		[Modal.DEEP_DUNGEON]: [],
		[Modal.FIELD_OPERATION]: [],
		[Modal.VARIANT]: [],
	},
	anonymousAvailable: false,
	gameServers: {},
};

export const appReducer = function(state:AppState = initialState, action:AnyAction){
	switch(action.type as ActionType){
		default: return state;
		case ActionType.SET_BREAKPOINT: return setBreakpoint(state, action.payload);
		case ActionType.SET_VIEW: return setView(state, action.payload);
		case ActionType.ADD_SYS_MESSAGE: return addSysMessage(state, action.payload);
		case ActionType.REMOVE_SYS_MESSAGE: return removeSysMessage(state, action.payload);
		case ActionType.SET_USER_INFO: return setUserInfo(state, action.payload);
		case ActionType.SET_USER: return setUser(state, action.payload);
		case ActionType.SET_USER_OPTIONS: return setUserOptions(state, action.payload);
		case ActionType.SET_USER_DATA: return setUserData(state, action.payload);
		case ActionType.SET_CHARACTERS: return setCharacters(state, action.payload);
		case ActionType.DELETE_CHARACTERS: return deleteCharacters(state, action.payload);
		case ActionType.SET_NOTICES: return setNotices(state, action.payload);
		case ActionType.SET_MODAL_CONFIRMATION: return setModalConfirmation(state, action.payload);
		case ActionType.SET_MODAL_ENEMIES: return setModalEnemies(state, action.payload);
		case ActionType.SET_MODAL_RECIPES: return setModalRecipes(state, action.payload);
		case ActionType.SET_MODAL_MARKETS: return setModalMarkets(state, action.payload);
		case ActionType.SET_MODAL_EXTRACTIONS: return setModalExtractions(state, action.payload);
		case ActionType.SET_MODAL_GATHERING: return setModalGathering(state, action.payload);
		case ActionType.SET_MODAL_FISHING: return setModalFishing(state, action.payload);
		case ActionType.SET_MODAL_SHOPS: return setModalShops(state, action.payload);
		case ActionType.SET_MODAL_LOCATIONS: return setModalLocations(state, action.payload);
		case ActionType.SET_MODAL_TRIALS: return setModalTrials(state, action.payload);
		case ActionType.SET_MODAL_DUNGEONS: return setModalDungeons(state, action.payload);
		case ActionType.SET_MODAL_RAIDS: return setModalRaids(state, action.payload);
		case ActionType.SET_MODAL_FATES: return setModalFates(state, action.payload);
		case ActionType.SET_MODAL_LEVES: return setModalLeves(state, action.payload);
		case ActionType.SET_MODAL_QUESTS: return setModalQuests(state, action.payload);
		case ActionType.SET_MODAL_OTHER: return setModalOther(state, action.payload);
		case ActionType.SET_MODAL_ADD_CHARACTER: return setModalAddCharacter(state, action.payload);
		case ActionType.SET_MODAL_ROULETTE: return setModalRoulette(state, action.payload);
		case ActionType.SET_MODAL_DEEP_DUNGEON: return setModalDeepDungeon(state, action.payload);
		case ActionType.SET_MODAL_FIELD_OPERATION: return setModalFieldOperation(state, action.payload);
		case ActionType.SET_MODAL_VARIANT: return setModalVariant(state, action.payload);
		case ActionType.SET_ANONYMOUS_AVAILABLE: return setAnonymousAvailable(state, action.payload);
		case ActionType.SET_GAME_SERVERS: return setGameServers(state, action.payload);
	}
};
export const store = createStore(appReducer);

function setBreakpoint(state:AppState, payload:Breakpoint):AppState{ return { ...state, breakpoint: payload }; }
function setView(state:AppState, payload:View):AppState{ return { ...state, view: payload }; }
function addSysMessage(state:AppState, payload:ISysMsg):AppState{ return { ...state, systemMessages: state.systemMessages.concat(payload) }; }
function removeSysMessage(state:AppState, payload:number):AppState{
	const index = state.systemMessages.findIndex((z) => { return z.id === payload; });
	if(index === -1){ return state; }
	return {
		...state,
		systemMessages: [
			...state.systemMessages.slice(0, index),
			...state.systemMessages.slice(index + 1),
		],
	};
}
function setUserInfo(state:AppState, payload:UserInfo):AppState{ return { ...state, userInfo: payload }; }
function setUser(state:AppState, payload:AppUser):AppState{ return { ...state, userInfo: { ...state.userInfo, isAuthenticated: payload.isAuthenticated, username: payload.username } }; }
function setUserOptions(state:AppState, payload:UserOptions):AppState{
	return { ...state, userInfo: { ...state.userInfo, options: payload } };
}
function setUserData(state:AppState, payload:UserData):AppState{ return { ...state, userInfo: { ...state.userInfo, data: payload } }; }
function setCharacters(state:AppState, payload:Character[]):AppState{
	const newCharState = [...state.userInfo.data.characters];
	payload.forEach((char) => {
		const index = newCharState.findIndex((z) => { return z.seId === char.seId; });
		if(index === -1){
			newCharState.push(char); // New Character
		}else{
			newCharState[index] = char;
		}
	});
	return { ...state, userInfo: { ...state.userInfo, data: { ...state.userInfo.data, characters: newCharState } } };
}
function deleteCharacters(state:AppState, payload:string[]):AppState{ return { ...state, userInfo: { ...state.userInfo, data: { ...state.userInfo.data, characters: state.userInfo.data.characters.filter((z) => { return !payload.includes(z.seId); }) } } }; }
function setNotices(state:AppState, payload:Notice[]):AppState{ return { ...state, notices: payload }; }
function setModalConfirmation(state:AppState, payload:ConfirmationOptions):AppState{ return { ...state, modal: { ...state.modal, [Modal.CONFIRMATION]: payload } }; }
function setModalEnemies(state:AppState, payload:Obtained.Enemy[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.ENEMIES]: payload } }; }
function setModalRecipes(state:AppState, payload:RecipeModalData):AppState{ return { ...state, modal: { ...state.modal, [Modal.RECIPES]: payload } }; }
function setModalMarkets(state:AppState, payload:Obtained.Market):AppState{ return { ...state, modal: { ...state.modal, [Modal.MARKETS]: payload } }; }
function setModalExtractions(state:AppState, payload:Obtained.Extraction):AppState{ return { ...state, modal: { ...state.modal, [Modal.EXTRACTIONS]: payload } }; }
function setModalGathering(state:AppState, payload:Obtained.Gathering[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.GATHERING]: payload } }; }
function setModalFishing(state:AppState, payload:Obtained.Fishing[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.FISHING]: payload } }; }
function setModalShops(state:AppState, payload:Obtained.Shop[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.SHOPS]: payload } }; }
function setModalLocations(state:AppState, payload:Obtained.Location[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.LOCATIONS]: payload } }; }
function setModalTrials(state:AppState, payload:Obtained.Trial[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.TRIALS]: payload } }; }
function setModalDungeons(state:AppState, payload:GameData.Dungeon[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.DUNGEONS]: payload } }; }
function setModalRaids(state:AppState, payload:GameData.Raid[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.RAIDS]: payload } }; }
function setModalFates(state:AppState, payload:Obtained.Fate[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.FATES]: payload } }; }
function setModalLeves(state:AppState, payload:GameData.Leve[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.LEVES]: payload } }; }
function setModalQuests(state:AppState, payload:GameData.Quest[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.QUESTS]: payload } }; }
function setModalOther(state:AppState, payload:Obtained.Other[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.OTHER]: payload } }; }
function setModalAddCharacter(state:AppState, payload:AddCharacterModalOptions):AppState{ return { ...state, modal: { ...state.modal, [Modal.ADD_CHARACTER]: payload }}; }
function setModalRoulette(state:AppState, payload:GameData.Roulette[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.ROULETTE]: payload }}; }
function setModalDeepDungeon(state:AppState, payload:GameData.DeepDungeon[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.DEEP_DUNGEON]: payload }}; }
function setModalFieldOperation(state:AppState, payload:GameData.FieldOperation[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.FIELD_OPERATION]: payload }}; }
function setModalVariant(state:AppState, payload:GameData.Variant[]):AppState{ return { ...state, modal: { ...state.modal, [Modal.VARIANT]: payload }}; }
function setAnonymousAvailable(state:AppState, payload:boolean):AppState{ return { ...state, anonymousAvailable: payload }; }
function setGameServers(state:AppState, payload:GameServers):AppState{ return { ...state, gameServers: payload }; }
